import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { BdGroupDisplayNamePipe } from '@bindoc-projects/bindoc-permissions-lib/ui';
import {
  BdShareablesService,
  granteeTypeLabel,
  SharePreview,
  SharePreviewResource,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  BD_SHARE_PERMISSION_DISPLAY_NAME,
  BdSharePermission,
  BdSharePermissionHelper,
  SHAREABLE_TYPE_DISPLAY_NAME,
  ShareableReference,
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';
import { TreeNode } from 'primeng/api';
import { Button } from 'primeng/button';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { TreeTableModule } from 'primeng/treetable';
import { take } from 'rxjs';

import {
  BdRequestAccessDialogComponent,
  RequestAccessDialogData,
} from '../request-access-dialog/request-access-dialog.component';

export interface PermissionDetailDialogData {
  shareableType: ShareableType;
  shareableId: string;
  shareableName?: string;
  shareTarget: ShareableReference;
  shareTargetName: string;
  shareTargetEmail?: string;
  allowedPermissions: BdSharePermission[];
  isCurrentUser: boolean;
}

interface DetailNodeData {
  resource: SharePreviewResource;
  status: 'ok' | 'warning' | 'error';
  permissionLabel: string;
  typeLabel: string;
}

@Component({
  selector: 'bd-permission-detail-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-3 py-4 mb-2">
        <div class="flex items-center gap-4">
          <div class="flex flex-col">
            <span class="text-xs text-muted-color">Ressource</span>
            <span class="font-medium">{{ data.shareableName }}</span>
          </div>
          <div>
            <i class="pi pi-arrows-h"></i>
          </div>
          <div class="flex flex-col">
            <span class="text-xs text-muted-color">{{
              shareTargetTypeLabel(data.shareTarget.type)
            }}</span>
            <span class="font-medium">{{
              (data.shareTarget | groupDisplayName | async) ||
                data.shareTargetName
            }}</span>
            @if (data.shareTargetEmail) {
              <span class="text-xs text-surface-400">{{
                data.shareTargetEmail
              }}</span>
            }
          </div>
        </div>
      </div>
      @if (errorState() !== 'none') {
        <div class="flex items-start gap-3" data-test="permission-detail-error">
          <i
            class="text-2xl text-bd-warning"
            [class.pi]="true"
            [class.pi-lock]="errorState() === 'forbidden'"
            [class.pi-exclamation-triangle]="errorState() === 'error'"
          ></i>
          <p class="m-0">
            @if (errorState() === 'forbidden') {
              Sie haben keine Berechtigung, die Freigabe-Details dieser
              Ressource anzuzeigen.
            } @else {
              Die Freigabe-Details konnten nicht geladen werden. Bitte versuchen
              Sie es später erneut.
            }
          </p>
        </div>
      } @else {
        <p-treetable [value]="treeNodes()" stripedRows>
          <ng-template #header>
            <tr>
              <th class="w-1/2">Ressource</th>
              <th class="w-1/4">Zugriff</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template #body let-rowNode let-rowData="rowData">
            <tr>
              <td>
                <div class="flex items-center gap-2">
                  <p-treetabletoggler [rowNode]="rowNode" />
                  <div class="flex flex-col">
                    <span>{{
                      rowData.resource.name ?? rowData.resource.id
                    }}</span>
                    <span class="text-xs text-muted-color">{{
                      rowData.typeLabel
                    }}</span>
                  </div>
                </div>
              </td>
              <td>
                <span [class]="statusClass(rowData.status)">
                  {{ statusLabel(rowData.status) }},
                  {{ rowData.permissionLabel }}
                </span>
              </td>
              <td>
                @if (rowData.status === 'warning' && data.isCurrentUser) {
                  <!--
                  A per-row action (02), and there can be one per missing
                  dependency — so it must not render as the view's single
                  filled primary, which is what omitting severity does.
                -->
                  <p-button
                    size="small"
                    severity="secondary"
                    label="Zugriff anfragen"
                    (click)="onRequestAccess(rowData.resource)"
                  ></p-button>
                }
              </td>
            </tr>
          </ng-template>
        </p-treetable>
      }
      <div class="flex flex-row-reverse w-full sticky-footer">
        <!-- 03 Abort: grey text. "outlined" is not a treatment in the styleguide. -->
        <p-button
          severity="secondary"
          text
          label="Schließen"
          (onClick)="close()"
        ></p-button>
      </div>
    </div>
  `,
  imports: [AsyncPipe, TreeTableModule, Button, BdGroupDisplayNamePipe],
})
export class BdPermissionDetailDialogComponent implements OnInit {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config =
    inject<DynamicDialogConfig<PermissionDetailDialogData>>(
      DynamicDialogConfig,
    );
  private readonly _resourcesService = inject(BdShareablesService);
  private readonly _dialogService = inject(DialogService);

  public readonly data: PermissionDetailDialogData = this._requireData();
  public readonly treeNodes = signal<TreeNode<DetailNodeData>[]>([]);
  /**
   * `'forbidden'` when share-preview returned 403 (the user lacks DELETE),
   * `'error'` for any other failure, `'none'` otherwise. Replaces the former
   * nameless "Fehler" row with a clear message (BDA-5507).
   */
  public readonly errorState = signal<'none' | 'forbidden' | 'error'>('none');

  ngOnInit(): void {
    this._runAnalysis();
  }

  public shareTargetTypeLabel(type: ShareableReference['type']): string {
    return granteeTypeLabel(type);
  }

  public statusLabel(status: DetailNodeData['status']): string {
    switch (status) {
      case 'ok':
        return 'Berechtigt';
      case 'warning':
        return 'Fehlt';
      case 'error':
        return 'Fehler';
    }
  }

  public statusClass(status: DetailNodeData['status']): string {
    switch (status) {
      case 'ok':
        return 'text-bd-success';
      case 'warning':
        return 'text-bd-warning';
      case 'error':
        return 'text-bd-error';
    }
  }

  public onRequestAccess(resource: SharePreviewResource): void {
    const data: RequestAccessDialogData = {
      shareableId: resource.id,
      shareableType: resource.type,
      shareableName: resource.name ?? undefined,
    };
    this._dialogService.open(BdRequestAccessDialogComponent, {
      header: 'Zugriff anfragen',
      data,
    });
  }

  public close(): void {
    this._ref.close();
  }

  private _runAnalysis(): void {
    this._resourcesService
      .sharePreview(
        this.data.shareableType,
        this.data.shareableId,
        this.data.shareTarget.id,
        this.data.shareTarget.type,
      )
      .pipe(take(1))
      .subscribe({
        next: (preview) => {
          this.errorState.set('none');
          this._buildTree(preview);
        },
        error: (err: unknown) => {
          this.treeNodes.set([]);
          this.errorState.set(
            err instanceof HttpErrorResponse && err.status === 403
              ? 'forbidden'
              : 'error',
          );
        },
      });
  }

  private _buildTree(preview: SharePreview): void {
    const permissionNames = BD_SHARE_PERMISSION_DISPLAY_NAME['de'];
    const root: TreeNode<DetailNodeData> = {
      data: {
        resource: preview.shareable,
        status: this._statusFor(preview.shareable),
        permissionLabel: this._formatPermissions(
          preview.shareable,
          permissionNames,
        ),
        typeLabel:
          SHAREABLE_TYPE_DISPLAY_NAME[preview.shareable.type] ??
          preview.shareable.type,
      },
      expanded: true,
      children: preview.dependencies.map((dep) => ({
        data: {
          resource: dep,
          status: this._statusFor(dep),
          typeLabel: SHAREABLE_TYPE_DISPLAY_NAME[dep.type] ?? dep.type,
          permissionLabel:
            dep.currentPermissions && dep.currentPermissions.length > 0
              ? permissionNames[
                  BdSharePermissionHelper.getHighestPermission(
                    dep.currentPermissions,
                  )
                ]
              : 'keine Berechtigung',
        },
      })),
    };
    this.treeNodes.set([root]);
  }

  private _statusFor(resource: SharePreviewResource): DetailNodeData['status'] {
    return resource.currentPermissions.length > 0 ? 'ok' : 'warning';
  }

  private _requireData(): PermissionDetailDialogData {
    if (!this._config.data) {
      throw new Error('BdPermissionDetailDialogComponent requires dialog data');
    }
    return this._config.data;
  }

  private _formatPermissions(
    resource: SharePreviewResource,
    permissionNames: Record<string, string>,
  ): string {
    if (resource.currentPermissions.length === 0) {
      return 'Kein Zugriff';
    }
    const highest = BdSharePermissionHelper.getHighestPermission(
      resource.currentPermissions,
    );
    return permissionNames[highest] ?? highest;
  }
}
